import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { BusinessContextProvider } from './contexts/BusinessContext';
import { PermissionContextProvider } from './contexts/PermissionContext';
import { MobileNavProvider } from './contexts/MobileNavContext';
import ErrorBoundary from './components/ErrorBoundary';
import LoginForm from './components/LoginForm';
import AuthCallback from './pages/AuthCallback';
import ActivationCodeVerification from './pages/ActivationCodeVerification';
import ProtectedRoute from './components/ProtectedRoute';
import EnhancedDashboard from './pages/EnhancedDashboard';
import BusinessProfile from './pages/BusinessProfile';
import ValuationCalculator from './pages/ValuationCalculator';
import ValuationDashboard from './pages/ValuationDashboard';
import Layout from './components/Layout';
import GapAnalysis from './pages/GapAnalysis';
import PersonalGapAnalysis from './pages/PersonalGapAnalysis';
import ExitStrategyQuiz from './pages/ExitStrategyQuiz';
import ExitStrategyResults from './pages/ExitStrategyResults';
import TaskManager from './pages/TaskManager';
import UserAccount from './pages/UserAccount';
import AdvisorDashboard from './pages/AdvisorDashboard';
import AcceptInvite from './pages/AcceptInvite';
import EnvisioningDashboard from './pages/EnvisioningDashboard';
import PrintValuationDashboard from './pages/Print/PrintValuationDashboard';
import PrintGapAnalysis from './pages/Print/PrintGapAnalysis';
import PrintClientProfile from './pages/Print/PrintClientProfile';
import PrintBusinessGapAssessment from './pages/Print/PrintBusinessGapAssessment';
import PrintPersonalGapAssessment from './pages/Print/PrintPersonalGapAssessment';
import PrintDashboard from './pages/Print/PrintDashboard';
import PrintWealthGapDetails from './pages/Print/PrintWealthGapDetails';
import PrintProfitGapDetails from './pages/Print/PrintProfitGapDetails';
import PrintValueGapDetails from './pages/Print/PrintValueGapDetails';
import PrintTasks from './pages/Print/PrintTasks';
import PrintBusinessEnvisioning from './pages/Print/PrintBusinessEnvisioning';
import PrintPersonalEnvisioning from './pages/Print/PrintPersonalEnvisioning';
import PrintAdvisoryGuide from './pages/Print/PrintAdvisoryGuide';
import PrintGoalSetting from './pages/Print/PrintGoalSetting';
import Inbox from './pages/Inbox';
import ExitReadinessAssessment from './pages/ExitReadinessAssessment';
import ExitReadinessResults from './pages/ExitReadinessResults';
import PrintExitReadinessReport from './pages/Print/PrintExitReadinessReport';
import ResetPassword from './pages/ResetPassword';
import ManageViewers from './pages/ManageViewers';
import NotificationSettingsPage from './pages/NotificationSettingsPage';

function App() {
  return (
    <AuthProvider>
      <TimezoneProvider>
        <BusinessContextProvider>
          <PermissionContextProvider>
            <MobileNavProvider>
              <Router>
                <ErrorBoundary>
                <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route path="/signup/verify-code" element={<ActivationCodeVerification />} />
              <Route path="/signup" element={<LoginForm />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/accept-invite/:token" element={<AcceptInvite />} />

          {/* Print-only routes for PDF generation (no layout, no auth guard) */}
          {/* These routes authenticate via token in URL parameter for Playwright PDF generation */}
          <Route path="/print/valuation/:type/:businessId" element={<PrintValuationDashboard />} />
          <Route path="/print/gap-analysis/:type/:businessId" element={<PrintGapAnalysis />} />
          <Route path="/print/client-profile/:businessId" element={<PrintClientProfile />} />
          <Route path="/print/business-gap-assessment/:businessId" element={<PrintBusinessGapAssessment />} />
          <Route path="/print/personal-gap-assessment/:businessId" element={<PrintPersonalGapAssessment />} />
          <Route path="/print/dashboard/:businessId" element={<PrintDashboard />} />
          <Route path="/print/wealth-gap-details/:businessId" element={<PrintWealthGapDetails />} />
          <Route path="/print/profit-gap-details/:businessId" element={<PrintProfitGapDetails />} />
          <Route path="/print/value-gap-details/:businessId" element={<PrintValueGapDetails />} />
          <Route path="/print/tasks/:businessId" element={<PrintTasks />} />
          <Route path="/print/business-envisioning/:businessId" element={<PrintBusinessEnvisioning />} />
          <Route path="/print/personal-envisioning/:businessId" element={<PrintPersonalEnvisioning />} />
          <Route path="/print/advisory-guide" element={<PrintAdvisoryGuide />} />
          <Route path="/print/goal-setting/:businessId" element={<PrintGoalSetting />} />

          {/* Public Lead Magnet Routes (no auth required) */}
          <Route path="/exit-readiness-assessment" element={<ExitReadinessAssessment />} />
          <Route path="/exit-readiness-results/:sessionToken" element={<ExitReadinessResults />} />
          <Route path="/public/exit-readiness-report/:sessionToken" element={<PrintExitReadinessReport />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnhancedDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/valuation-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <ValuationDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/business-profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <BusinessProfile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/valuation"
            element={
              <ProtectedRoute>
                <Layout>
                  <ValuationCalculator />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gap-analysis"
            element={
              <ProtectedRoute>
                <Layout>
                  <GapAnalysis />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/personal-gap-analysis"
            element={
              <ProtectedRoute>
                <Layout>
                  <PersonalGapAnalysis />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/exit-strategy-quiz"
            element={
              <ProtectedRoute>
                <Layout>
                  <ExitStrategyQuiz />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/exit-strategy-results"
            element={
              <ProtectedRoute>
                <Layout>
                  <ExitStrategyResults />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/task-manager"
            element={
              <ProtectedRoute>
                <Layout>
                  <TaskManager />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserAccount />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/advisor"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdvisorDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/advisor/settings/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <NotificationSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/manage-viewers"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManageViewers />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/envisioning"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnvisioningDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <Layout>
                  <Inbox />
                </Layout>
              </ProtectedRoute>
            }
          />

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ErrorBoundary>
        </Router>
          </MobileNavProvider>
        </PermissionContextProvider>
        </BusinessContextProvider>
      </TimezoneProvider>
    </AuthProvider>
  );
}

export default App;
